import { useState, useEffect, useCallback, useMemo, createContext, useContext } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "darkrequiem_token";

// Attach Authorization header to every axios request when we have a token
const applyTokenHeader = (token) => {
  if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete axios.defaults.headers.common["Authorization"];
};

const initialToken = localStorage.getItem(TOKEN_KEY);
applyTokenHeader(initialToken);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);      // null=not logged in, object=logged in
  const [guest, setGuest] = useState(false);   // playing as guest
  const [loading, setLoading] = useState(true);
  const [token, setTokenState] = useState(initialToken);

  const setToken = useCallback((t) => {
    setTokenState(t);
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
    applyTokenHeader(t);
  }, []);

  // On mount, check for existing token OR incoming Emergent Google session_id
  useEffect(() => {
    // Google callback: URL fragment contains #session_id=...
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const hash = window.location.hash || "";
    const sessionMatch = hash.match(/session_id=([^&]+)/);
    if (sessionMatch) {
      const sid = sessionMatch[1];
      // Clear the hash so refresh doesn't retrigger
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      axios.post(`${API}/auth/google`, { session_id: sid })
        .then((r) => {
          setToken(r.data.token);
          setUser(r.data.user);
          setGuest(false);
        })
        .catch((e) => {
          console.warn("Google session exchange failed", e?.response?.data || e);
        })
        .finally(() => setLoading(false));
      return;
    }

    if (!initialToken) {
      // Auto-restore guest mode if flagged
      if (localStorage.getItem("darkrequiem_guest") === "1") setGuest(true);
      setLoading(false);
      return;
    }
    axios.get(`${API}/auth/me`)
      .then((r) => { setUser(r.data); setGuest(false); })
      .catch(() => { setToken(null); })
      .finally(() => setLoading(false));
  }, [setToken]);

  const login = useCallback(async (username, password) => {
    const r = await axios.post(`${API}/auth/login`, { username, password });
    setToken(r.data.token);
    setUser(r.data.user);
    setGuest(false);
    localStorage.removeItem("darkrequiem_guest");
    return r.data.user;
  }, [setToken]);

  const register = useCallback(async (username, password) => {
    const r = await axios.post(`${API}/auth/register`, { username, password });
    setToken(r.data.token);
    setUser(r.data.user);
    setGuest(false);
    localStorage.removeItem("darkrequiem_guest");
    return r.data.user;
  }, [setToken]);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setGuest(false);
    localStorage.removeItem("darkrequiem_guest");
    // Wipe local game save so next login starts clean
    localStorage.removeItem("darkrequiem_save_v2");
  }, [setToken]);

  const enterAsGuest = useCallback(() => {
    setGuest(true);
    localStorage.setItem("darkrequiem_guest", "1");
  }, []);

  const value = useMemo(() => ({
    user, guest, loading, token,
    login, register, logout, loginWithGoogle, enterAsGuest,
  }), [user, guest, loading, token, login, register, logout, loginWithGoogle, enterAsGuest]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
